/**
 * AddChannelModal Component
 *
 * Modal for adding new sales channels. The setup UI branch is driven
 * entirely by the selected connector's declared `capabilities[]` via
 * `resolveSetupPanelKind` — there is no per-provider
 * id-switch (no `channelRegistrationMap` / `channelHandlerMap`).
 *
 * Panel kinds:
 * - `oauth` → OAuthPanel (collects store URL inline when the connector
 *   declares `settings.needsUrl`, e.g. tiendanube/shopify/fenicio).
 * - `apiKey` → ApiKeyPanel with a connector-specific field schema.
 * - `directProvision` → DirectProvisionPanel + nested RetailStoreConfigModal.
 * - `serverConfigured` → ServerConfiguredPanel (read-only, no submit).
 *
 * Form state lives in local React `useState` (per ticket decision); no new
 * CDSE commands/handlers are introduced for the credential fields.
 *
 * OAuth flows are initiated here and completed via AddChannelRedirect — the
 * redirect logic is unchanged from the legacy modal.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uuidv7 } from 'uuidv7';
import { Button, Checkbox, Group, Image, LoadingOverlay, Modal, Select, SelectProps, Stack, Text, Title } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { toast } from '@components/common/Toast/Toast';
import { getLocalizableLabel } from '@utils/channels';
import { WarehousesReadmodelType, PriceListReadmodelType } from '@bett3r-dev/teselly-domain';
import OAuthPanel from '../../../components/OAuthPanel';
import ApiKeyPanel, { ApiKeyField } from '../../../components/ApiKeyPanel';
import DirectProvisionPanel from '../../../components/DirectProvisionPanel';
import ServerConfiguredPanel from '../../../components/ServerConfiguredPanel';
import RetailStoreConfigModal from '../../../components/RetailStoreConfigModal';
import { canImportOrderHistory, resolveSetupPanelKind } from '../../../shared/state/capabilityGates';
import { requestOAuthAuthorizationUrl } from '../../../shared/state/oauthAuthorizationUrl';
import { useSharedIntegrationsSelector, selectors as sharedSelectors } from '../../../shared/state/store';
import { useChannelsDispatch, useChannelsSelector, selectors } from '../../state/store';
import { integrationsTestIds } from '@domains/integrations/testids';
import styles from './AddChannelModal.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AddChannelModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * Connector to land on when the modal opens, instead of the catalog's first
   * entry. Set when the user already chose a channel somewhere else and this
   * modal is only finishing the job — today that's the assistant's
   * `channel-picker` block, which hands the choice over through
   * `/integrations/channels?connect=<connectorId>`.
   *
   * Ignored if the id isn't in the ( flag-filtered ) catalog, so a stale link
   * degrades to the normal first-entry behaviour rather than an empty modal.
   */
  preselectedConnectorId?: string;
}

/**
 * Store-URL placeholders, keyed by connector id. Used only as input copy for
 * the OAuth store-URL field — NOT to decide whether the field renders (that
 * is driven by the connector's declared `settings.needsUrl`).
 */
const storeUrlPlaceholderMap: Record<string, string> = {
  tiendanube: 'mitienda.mitiendanube.com',
  shopify: 'mitienda.myshopify.com',
  fenicio: 'mi-tienda-fenicio.fenicio.com.uy'
};

/**
 * API-key credential field schema, keyed by connector id. Different api-key
 * providers expose different field names, so a narrow per-connector schema is
 * acceptable here (it carries field shape only, not branching logic). The
 * panel kind itself is still resolved from capabilities, not this map.
 */
/**
 * Exported (TV1-ONBOARDING): the onboarding wizard collects the same
 * credentials from its own bento panel, and a second copy of this per-connector
 * field list is how the two surfaces end up asking for different fields for the
 * same connector. One map, two callers.
 */
export const apiKeyFieldSchemas = (
  t: ( key: string, fallback: string ) => string
): Record<string, ApiKeyField[]> => ({
  woocommerce: [
    {
      name: 'storeUrl',
      label: t( 'add_channel_modal_channel_url_label', 'URL de la tienda' ),
      placeholder: 'mitienda.com',
      isUrl: true
    },
    {
      name: 'consumerKey',
      label: t( 'add_channel_modal_woocommerce_consumer_key_label', 'Consumer Key' ),
      placeholder: 'ck_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
    },
    {
      name: 'consumerSecret',
      label: t( 'add_channel_modal_woocommerce_consumer_secret_label', 'Consumer Secret' ),
      placeholder: 'cs_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
      secret: true
    }
  ],
  amazon: [
    {
      name: 'sellerId',
      label: t( 'add_channel_modal_amazon_seller_id_label', 'SellerId' ),
      placeholder: 'A2Q1234567890'
    },
    {
      name: 'refreshToken',
      label: t( 'add_channel_modal_amazon_refresh_token_label', 'Refresh Token' ),
      placeholder: '',
      secret: true
    }
  ]
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const AddChannelModal = ({ opened, onClose, preselectedConnectorId }: AddChannelModalProps ) => {
  const { t, i18n } = useTranslation( 'channels' );
  const { backend } = useBackend() as BackendContextType;
  const dispatch = useChannelsDispatch();

  // State from stores
  const connectors = useSharedIntegrationsSelector( sharedSelectors.salesChannelConnectors );
  const isLoadingConnectors = useSharedIntegrationsSelector( sharedSelectors.connectorsLoading );
  const isSubmitting = useChannelsSelector( selectors.isSubmitting );

  // Feature-flag gate for the testchannel connector. Default false ensures the
  // option never flashes in staging/prod before the OrgResolvedFlags snapshot
  // settles (ProvideFeatureFlags blocks rendering until then; this default is a
  // defence-in-depth measure matching the FlaggedRoute/ConnectorList pattern).
  const isTestChannelEnabled = useBooleanFlagValue( 'test-channel-connector', false );

  // Local form state (per ticket decision — not threaded to domain state)
  const [ selectedChannel, setSelectedChannel ] = useState<string>( '' );
  const [ storeUrl, setStoreUrl ] = useState( '' );
  const [ apiKeyValues, setApiKeyValues ] = useState<Record<string, string>>({});
  const [ storeName, setStoreName ] = useState( '' );
  const [ modalWarehouses, setModalWarehouses ] = useState<WarehousesReadmodelType[]>([]);
  const [ modalPriceLists, setModalPriceLists ] = useState<PriceListReadmodelType[]>([]);
  const [ warehouseIds, setWarehouseIds ] = useState<string[]>([]);
  const [ defaultChannelWarehouseId, setDefaultChannelWarehouseId ] = useState<string | null>( null );
  const [ priceListIds, setPriceListIds ] = useState<string[]>([]);
  const [ defaultPriceListId, setDefaultPriceListId ] = useState<string | null>( null );
  const [ isConfigModalOpen, setConfigModalOpen ] = useState( false );

  // Derived (recomputed every render — no `activeSetupCapability` in state).
  // testchannel is gated by the `test-channel-connector` flag so the option is
  // only visible when explicitly enabled (e.g. local dev, opted-in staging).
  const connectorIds = Object.keys( connectors ).filter(
    ( id ) => isTestChannelEnabled || id !== 'testchannel'
  );
  const selectedConnector = connectors[selectedChannel];
  const panelKind = resolveSetupPanelKind( selectedConnector );
  const connectorLabel = getLocalizableLabel( selectedConnector, i18n.language );

  // The warehouse / price-list association is a retailstore-only concern. Other
  // direct-provision connectors (e.g. testchannel) share the panel for the name
  // field but must NOT default a stock/price-list association — their channel
  // inventory is configured later via the setup wizard. Mirrors how ChannelInfo
  // scopes those detail rows to `retailstore`.
  const isRetailStore = selectedChannel === 'retailstore';

  // Whether the OAuth flow needs a store URL collected before redirect. Driven
  // by the connector's declared `settings.needsUrl` (true for tiendanube /
  // shopify / fenicio) — NOT a hardcoded connector-id list.
  const requiresStoreUrl = Boolean( selectedConnector?.settings?.needsUrl );

  /*
   * TV1-2354 — auto-import offer. Default ON: connecting a sales channel almost
   * always means wanting its existing listings in Teselly, and the import is
   * additive and re-runnable.
   */
  const [ autoImportPublications, setAutoImportPublications ] = useState( true );
  const showAutoImportOption =
    panelKind !== 'serverConfigured' && selectedChannel !== 'retailstore';

  /*
   * TV1-2379 — historical order import. Default OFF, unlike the publications
   * box above, and the asymmetry is the point: pulling a seller's entire past
   * order history is heavy and effectively one-way, where pulling their current
   * listings is additive and re-runnable. Opt in, not opt out.
   *
   * Offered only for connectors that can count the history they are about to
   * pull — `canImportOrderHistory`. The backend rejects the rest outright with
   * BATCH_PROCESSES_CHANNEL_CAPABILITY_UNSUPPORTED, so showing the box for them
   * would be offering a button that 400s.
   */
  /*
   * Default ON, matching `autoImportPublications` — changed from OFF on the product
   * owner's call (TV1-2379). It reads like the riskier default until you follow what
   * a seeded order actually does: nothing. They are inert history — no invoices, no
   * stock movements, no buyer messages, no channel writes — because
   * `channelOrderImport.policy.ts` stamps them as bulk-sync writes and the rules
   * producers skip them. What is left is rows in Orders, which is the thing the
   * seller connected the channel to see.
   */
  const [ autoImportOrders, setAutoImportOrders ] = useState( true );
  const showOrderImportOption =
    showAutoImportOption && canImportOrderHistory( selectedChannel );

  // API-key field schema for the selected connector (field shape only).
  const apiKeyFields = useMemo<ApiKeyField[]>(
    () => apiKeyFieldSchemas( t )[selectedChannel] ?? [],
    [ t, selectedChannel ]
  );

  // Auto-select the first channel once the catalog loads. Also resets the
  // selection if the current `selectedChannel` is no longer in `connectorIds`
  // — e.g. testchannel was selected and the flag flipped off, which would
  // otherwise leave the modal rendering with a stale invalid value.
  useEffect(() => {
    if ( connectorIds.length === 0 ) return;
    if ( !connectorIds.includes( selectedChannel )) {
      // A caller-supplied preselection wins over the first entry, but only
      // while it is actually offered — otherwise this is the same fallback.
      const preselected =
        preselectedConnectorId && connectorIds.includes( preselectedConnectorId )
          ? preselectedConnectorId
          : connectorIds[0];
      setSelectedChannel( preselected );
    }
  }, [ selectedChannel, connectorIds, preselectedConnectorId ]);

  /**
   * Re-open with a DIFFERENT preselection: the effect above only fires when the
   * current selection falls out of the catalog, so without this a second
   * hand-off would land on whatever the previous one left selected.
   *
   * ⚠️ Applied ONCE per open, tracked in a ref. `connectorIds` is rebuilt on
   * every render (it is a `.filter()` over `connectors`, not memoised), so an
   * unguarded effect re-runs every render and re-applies the preselection —
   * pinning the select so the user can never change the channel away from the
   * one they arrived with.
   */
  const appliedPreselection = useRef<string | null>( null );

  useEffect(() => {
    if ( !opened ) {
      appliedPreselection.current = null;
      return;
    }
    if ( !preselectedConnectorId ) return;
    if ( appliedPreselection.current === preselectedConnectorId ) return;
    if ( !connectorIds.includes( preselectedConnectorId )) return;

    appliedPreselection.current = preselectedConnectorId;
    setSelectedChannel( preselectedConnectorId );
  }, [ opened, preselectedConnectorId, connectorIds ]);

  // Fetch warehouses + price lists for the retailstore direct-provision flow
  // only — other direct-provision connectors don't carry a stock association.
  useEffect(() => {
    if ( panelKind !== 'directProvision' || !isRetailStore ) return;
    backend.queries
      .StockManagementWarehouses()
      .then(( result ) => {
        if ( result ) setModalWarehouses( result );
      })
      .catch(() => {});
    backend.queries
      .ItemsPublishingPriceLists()
      .then(( result ) => {
        if ( result ) setModalPriceLists( result );
      })
      .catch(() => {});
  }, [ panelKind, isRetailStore, backend ]);

  // ───────────────────────────────────────────────────────────────────────────
  // Field handlers
  // ───────────────────────────────────────────────────────────────────────────

  const resetForm = () => {
    setStoreUrl( '' );
    setApiKeyValues({});
    setStoreName( '' );
    setWarehouseIds([]);
    setDefaultChannelWarehouseId( null );
    setPriceListIds([]);
    setDefaultPriceListId( null );
  };

  const handleChannelChange = ( value: string | null ) => {
    if ( !value ) return;
    setSelectedChannel( value );
    resetForm();
  };

  const handleApiKeyChange = ( name: string, value: string ) => {
    setApiKeyValues(( prev ) => ({ ...prev, [name]: value }));
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Submit — one branch per panel kind (no connector-id switch)
  // ───────────────────────────────────────────────────────────────────────────

  const setSubmitting = ( isLoading: boolean ) => {
    dispatch(
      { type: 'integrations/channels/loading/setSubmitting', payload: { isLoading }},
      { source: 'user' }
    );
  };

  const submitOAuth = async () => {
    setSubmitting( true );
    try {
      const cleaned = storeUrl.trim().replace( /^https?:\/\//, '' );
      // Add channel carries no `expectedIntegrationUserId`, so any seller may be linked
      // (Reconnect is `startChannelReconnect`, TV1-2446 slice 5).
      const url = await requestOAuthAuthorizationUrl(
        backend.get,
        requiresStoreUrl
          ? { connectorId: selectedChannel, storeUrl: `https://${cleaned}` }
          : { connectorId: selectedChannel, site: 'MLA' }
      );
      if ( url ) {
        window.open( url, '_blank', 'noopener,noreferrer' );
      }
      // Only close on success — a thrown error keeps the modal open so the
      // global error handler can surface it without losing the user's input.
      onClose();
    } finally {
      setSubmitting( false );
    }
  };

  const submitApiKey = async () => {
    setSubmitting( true );
    try {
      // Server-side authorization validates the credentials against the
      // provider API, stores them, and provisions the integration. The FE never
      // provisions directly — see teselly-sales-channels woocommerce.authorization.ts.
      await backend.post.systems_integrations_webhooks_post(
        { connectorId: selectedChannel, route: 'authorize' },
        apiKeyValues
      );
      toast.success({
        title: t( 'add_channel_modal_apikey_success_title', 'Canal conectado' ),
        message: t(
          'add_channel_modal_apikey_success_message',
          'Validamos las credenciales y conectamos el canal.'
        )
      });
      // Only close on success — a thrown error (invalid credentials, or a
      // connector without api-key authorization) keeps the modal open and is
      // surfaced by the global error handler.
      onClose();
    } finally {
      setSubmitting( false );
    }
  };

  const submitDirectProvision = async () => {
    setSubmitting( true );
    try {
      const integrationUserId = uuidv7();
      await backend.commands.ProvisionSystemIntegration({
        id: `${selectedChannel}_${integrationUserId}`,
        body: {
          connectorId: selectedChannel,
          integrationUserId,
          channelUserName: storeName,
          label: storeName,
          // Stock/price-list association is retailstore-only; other
          // direct-provision connectors provision with empty settings and are
          // configured later via the setup wizard.
          settings: isRetailStore
            ? {
                warehouses: warehouseIds,
                defaultChannelWarehouseId,
                priceLists: priceListIds,
                defaultPriceListId
              }
            : {}
        }
      });
      // Only close on success — see submitOAuth.
      onClose();
    } finally {
      setSubmitting( false );
    }
  };

  const handleSubmit = () => {
    // Park the intent BEFORE the submit branches: the OAuth path navigates away
    // and the api-key path resolves server-side, so this is the last moment all
    // three share. `channelAutoImportPolicy` picks it up when the channel lands.
    if ( showAutoImportOption && autoImportPublications ) {
      dispatch(
        {
          type: 'integrations/channels/autoImport/setPending',
          payload: { connectorId: selectedChannel, kind: 'publications' }
        },
        { source: 'user' }
      );
    }
    if ( showOrderImportOption && autoImportOrders ) {
      dispatch(
        {
          type: 'integrations/channels/autoImport/setPending',
          payload: { connectorId: selectedChannel, kind: 'orders' }
        },
        { source: 'user' }
      );
    }
    switch ( panelKind ) {
      case 'oauth':
        return submitOAuth();
      case 'apiKey':
        return submitApiKey();
      case 'directProvision':
        return submitDirectProvision();
      default:
        // serverConfigured has no submit action.
        return undefined;
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Validation — derived per panel kind (no connector-id switch)
  // ───────────────────────────────────────────────────────────────────────────

  const apiKeyAllFilled = apiKeyFields.every(
    ( field ) => ( apiKeyValues[field.name] ?? '' ).trim().length > 0
  );

  const isContinueDisabled =
    !selectedConnector ||
    ( panelKind === 'oauth' && requiresStoreUrl && !storeUrl.trim()) ||
    ( panelKind === 'apiKey' && !apiKeyAllFilled ) ||
    ( panelKind === 'directProvision' && !storeName.trim());

  // serverConfigured is read-only — no submit/continue action.
  const showFooterSubmit = panelKind !== 'serverConfigured';

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────

  const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => (
    <Group
      flex="1"
      gap="xs"
      data-testid={integrationsTestIds.connectorRow( option.value )}
    >
      <Image
        h={24}
        w="auto"
        src={connectors[option.value]?.imageUrl}
        alt="channel-logo"
        fallbackSrc={`https://placehold.co/24x24?text=image`}
        radius="sm"
      />
      <Text size="sm">{option.label}</Text>
      {checked && (
        <IconCheck
          stroke={1.5}
          color="currentColor"
          size={16}
          opacity={0.6}
          style={{ marginInlineStart: 'auto' }}
        />
      )}
    </Group>
  );

  const isConfigured = warehouseIds.length > 0 || priceListIds.length > 0;

  const renderSetupPanel = () => {
    if ( !selectedConnector ) return null;

    switch ( panelKind ) {
      case 'oauth':
        return (
          <OAuthPanel
            connectorId={selectedChannel}
            connectorLabel={connectorLabel}
            requiresStoreUrl={requiresStoreUrl}
            storeUrl={storeUrl}
            onStoreUrlChange={setStoreUrl}
            storeUrlPlaceholder={storeUrlPlaceholderMap[selectedChannel]}
          />
        );
      case 'apiKey':
        return (
          <ApiKeyPanel
            fields={apiKeyFields}
            values={apiKeyValues}
            onChange={handleApiKeyChange}
          />
        );
      case 'directProvision':
        return (
          <DirectProvisionPanel
            connectorId={selectedChannel}
            connectorLabel={connectorLabel}
            name={storeName}
            onNameChange={setStoreName}
            showInventoryConfig={isRetailStore}
            isConfigured={isConfigured}
            warehouseIds={warehouseIds}
            priceListIds={priceListIds}
            availableWarehouses={modalWarehouses}
            availablePriceLists={modalPriceLists}
            defaultChannelWarehouseId={defaultChannelWarehouseId}
            defaultPriceListId={defaultPriceListId}
            onOpenConfig={() => setConfigModalOpen( true )}
            disabled={isSubmitting}
          />
        );
      case 'serverConfigured':
        return (
          <ServerConfiguredPanel
            connectorId={selectedChannel}
            connectorLabel={connectorLabel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        size="md"
        withCloseButton={false}
        yOffset="15dvh"
        closeOnClickOutside
        trapFocus={false}
        classNames={{ content: styles.modal }}
      >
        <LoadingOverlay
          visible={isSubmitting || isLoadingConnectors}
          zIndex={2}
          overlayProps={{ radius: 'sm', blur: 2 }}
        />
        <Stack align="center" gap="md" className={styles.stack}>
          <Title order={2} size={20}>
            {t( 'add_channel_modal_title', 'Agregar Canal' )}
          </Title>
          <Text ta="center" c="dimmed" size="xs">
            {panelKind === 'oauth'
              ? t(
                  'add_channel_modal_description',
                  'Selecciona uno de los canales disponibles, a continuación te redireccionará a la web correspondiente para continuar el proceso.'
                )
              : t(
                  'add_channel_modal_description_no_redirect',
                  'Selecciona uno de los canales disponibles y completa su configuración para conectarlo.'
                )}
          </Text>

          <Select
            size="md"
            data={connectorIds.map(( connectorId ) => ({
              value: connectorId,
              label: getLocalizableLabel( connectors[connectorId], i18n.language )
            }))}
            value={selectedChannel}
            onChange={handleChannelChange}
            renderOption={renderSelectOption}
            allowDeselect={false}
            checkIconPosition="right"
            className={styles.select}
            leftSection={
              <Image
                h={24}
                w="auto"
                src={connectors[selectedChannel]?.imageUrl}
                alt="channel-logo"
                fallbackSrc={`https://placehold.co/24x24?text=image`}
                radius="sm"
              />
            }
          />

          {renderSetupPanel()}

          {/*
            TV1-2354 — "importar publicaciones automáticamente", offered at connect
            time for every connector that HAS a listing catalog.

            `serverConfigured` is excluded because it has no submit action at all,
            and `retailstore` because a physical point of sale has no channel-side
            listings to pull (the manual import is hidden for it too).

            Ticking this does not import anything here: for OAuth and api-key the
            integration does not exist yet when this modal closes. The choice is
            parked in the channels store and `channelAutoImportPolicy` fires one
            import when the channel arrives over the subscription. See that policy
            for why all three connect paths need the same deferral.
          */}
          {showAutoImportOption && (
            <Checkbox
              checked={autoImportPublications}
              onChange={( e ) => setAutoImportPublications( e.currentTarget.checked )}
              label={t(
                'add_channel_modal_auto_import_label',
                'Importar publicaciones automáticamente'
              )}
              description={t(
                'add_channel_modal_auto_import_description',
                'Al conectar, traemos las publicaciones que ya existen en el canal. Vas a poder seguir el progreso en la lista de publicaciones.'
              )}
              className={styles.autoImportCheckbox}
              data-testid={integrationsTestIds.addChannelAutoImportPublications}
            />
          )}

          {/*
            TV1-2379 — "importar el historial de pedidos", beside the publications
            box and gated more tightly than it.

            Only connectors that can COUNT the history they are about to pull are
            offered it (`canImportOrderHistory`); the rest are rejected outright by
            the aggregate. Default OFF: a seller's whole order history is a heavy,
            effectively one-way pull, unlike the additive, re-runnable listing
            import above — so this one is opt in.

            Same deferral as the publications box: nothing is imported here, the
            choice is parked per-connector and `channelAutoImportPolicy` fires one
            batch process when the channel arrives.
          */}
          {showOrderImportOption && (
            <Checkbox
              checked={autoImportOrders}
              onChange={( e ) => setAutoImportOrders( e.currentTarget.checked )}
              label={t(
                'add_channel_modal_auto_import_orders_label',
                'Importar el historial de pedidos'
              )}
              description={t(
                'add_channel_modal_auto_import_orders_description',
                'Traemos todos los pedidos que ya existen en el canal, desde el primero. Corre en segundo plano y podés seguir el progreso en Procesos por lotes. Los pedidos importados quedan como historial: no facturan, no mueven stock ni le escriben al comprador.'
              )}
              className={styles.autoImportCheckbox}
              data-testid={integrationsTestIds.addChannelAutoImportOrders}
            />
          )}
        </Stack>

        <div className={styles.fixedFooter}>
          <Group justify="space-between" gap="sm" className={styles.buttonGroup}>
            <Button variant="light" size="md" className={styles.cancelButton} onClick={onClose}>
              {t( 'add_channel_modal_cancel_button', 'Cancelar' )}
            </Button>
            {showFooterSubmit && (
              <Button
                size="md"
                className={`${styles.continueButton} ${isContinueDisabled ? styles.continueButtonDisabled : ''}`}
                onClick={handleSubmit}
                disabled={isContinueDisabled || isSubmitting}
                data-testid={integrationsTestIds.channelsContinueButton}
              >
                {t( 'add_channel_modal_continue_button', 'Continuar' )}
              </Button>
            )}
          </Group>
        </div>
      </Modal>

      <RetailStoreConfigModal
        opened={isConfigModalOpen}
        onClose={() => setConfigModalOpen( false )}
        availableWarehouses={modalWarehouses}
        availablePriceLists={modalPriceLists}
        warehouseIds={warehouseIds}
        defaultChannelWarehouseId={defaultChannelWarehouseId}
        onWarehousesChange={( ids, defaultId ) => {
          setWarehouseIds( ids );
          setDefaultChannelWarehouseId( defaultId );
        }}
        priceListIds={priceListIds}
        defaultPriceListId={defaultPriceListId}
        onPriceListsChange={( ids, defaultId ) => {
          setPriceListIds( ids );
          setDefaultPriceListId( defaultId );
        }}
      />
    </>
  );
};

export default AddChannelModal;
